import { Controller, useFormContext } from 'react-hook-form';
import { Button, Tooltip } from 'shared-ui';

import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { MultiAutocomplete } from 'ui/multiAutocomplete/MultiAutocomplete';
import { ActionModal } from 'ui/actionModal/ActionModal';
import { useRenderMultiAutocompleteOption } from 'ui/multiAutocomplete/hooks/useRenderMultiAutocompleteOption';
import { Form } from 'ui/form/Form';
import { useAppSelectOptions } from 'hooks/useAppSelectOptions/useAppSelectOptions';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { Functionality } from 'api/userPermissions/userPermissions.enum';

import { UpdateMultiRandomControlForm, UpdateMultiRandomControlProps } from './UpdateMultiRandomControl.types';

export const UpdateMultiRandomControl = ({
  servers,
  terminals,
  isLoadingTerminals,
  isModalOpen,
  onModalClose,
  onModalOpen,
  onSubmit,
  isLoadingUpdateControl,
  checkedOwners,
}: UpdateMultiRandomControlProps) => {
  const { formatMessage } = useLocale();
  const { renderMultiAutocompleteOption } = useRenderMultiAutocompleteOption();
  const { perms } = usePermissions();
  const isPermsDisabled = !perms?.[Functionality.KONTROLA]?.edit;

  const {
    control,
    setValue,
    handleSubmit,
    formState: { isValid },
  } = useFormContext<UpdateMultiRandomControlForm>();

  const getTerminalsOptions = () => {
    return terminals.map((terminal) => ({
      label: terminal.nazwa,
      value: terminal.id,
      number: terminal.numerLogicznyKontrolera,
    }));
  };

  const { serverOptions } = useAppSelectOptions({ servers });

  return (
    <>
      <Tooltip
        placement="bottom-start"
        disableHoverListener={!isPermsDisabled}
        title={formatMessage({ id: AppMessages['common.noPermissions'] })}
      >
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          startIcon={<EditIcon />}
          onClick={onModalOpen}
          disabled={isPermsDisabled}
        >
          {formatMessage({ id: AppMessages['control.random.multiAction.update.button'] })}
        </Button>
      </Tooltip>

      <ActionModal
        size="medium"
        header={formatMessage({ id: AppMessages['control.random.multiAction.update.title'] })}
        onClose={onModalClose}
        open={isModalOpen}
        onSubmit={handleSubmit(onSubmit)}
        submitButtonDisabled={!isValid || isLoadingUpdateControl}
        submitButtonText={formatMessage({ id: AppMessages['control.random.multiAction.update.submit'] })}
      >
        <ActionModal.Subtitle>
          {formatMessage({ id: AppMessages['control.random.multiAction.update.subtitle'] })} {checkedOwners.length}
        </ActionModal.Subtitle>

        <Form.Grid>
          <Form.ControlledSelect
            name="serwerId"
            control={control}
            options={serverOptions}
            onChange={(e) => {
              setValue('serwerId', e.target.value as string, { shouldDirty: true });
              setValue('tkds', []);
            }}
            label={formatMessage({ id: AppMessages['control.random.multiAction.update.server.label'] })}
            withHelperText
            required
          />

          <Controller
            name="tkds"
            control={control}
            render={({ field }) => (
              <MultiAutocomplete
                {...field}
                required
                withLimitedTags
                stopRotatePopupIndicator
                inputSize="big"
                limitTags={1}
                onChange={(_, option) => (option ? field.onChange(option) : field.onChange(null))}
                options={getTerminalsOptions()}
                loading={isLoadingTerminals}
                label={formatMessage({ id: AppMessages['control.random.multiAction.update.readers.label'] })}
                placeholder={formatMessage({
                  id: AppMessages['control.random.multiAction.update.readers.placeholder'],
                })}
                renderOption={renderMultiAutocompleteOption}
              />
            )}
          />
        </Form.Grid>
      </ActionModal>
    </>
  );
};
